import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface AirportData {
  code: string;
  name: string;
  city: string;
  totalPassengers: number;
  distanceMiles?: number;
}

interface AirportTrafficChartProps {
  data: AirportData[];
  startYear: number | null;
  startMonth: number | null;
  endYear: number | null;
  endMonth: number | null;
  loading?: boolean;
  title?: string;
}

export const AirportTrafficChart: React.FC<AirportTrafficChartProps> = ({
  data,
  startYear,
  startMonth,
  endYear,
  endMonth,
  loading = false,
  title = 'Last 12 Months Airport Passenger Traffic',
}) => {
  const theme = useTheme();

  // Color for bars
  const barColor = '#0050ad'; // Matching the ADR color from color scheme

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const airport = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {airport.code} - {airport.name}
          </Typography>
          {airport.city && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {airport.city}
            </Typography>
          )}
          <Typography variant="body2" color="primary">
            Passengers: {payload[0].value.toLocaleString()}
          </Typography>
          {airport.distanceMiles !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Distance: {airport.distanceMiles.toFixed(1)} mi
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  // Format the data for the chart - use airport code as the key
  const chartData = React.useMemo(() => {
    return data.map(airport => ({
      ...airport,
      displayName: airport.code,
    }));
  }, [data]);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            {title}
          </Typography>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No airport traffic data available for this market
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          {title}
        </Typography>

        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis
                dataKey="displayName"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                stroke={theme.palette.text.secondary}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(0)}K`;
                  }
                  return value.toString();
                }}
                label={{
                  value: 'Total Passengers',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />

              <Bar dataKey="totalPassengers" radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={barColor} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Total arriving passengers at top 3 nearby airports (12-month period)
        </Typography>
        {startYear && startMonth && endYear && endMonth && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Period: {new Date(startYear, startMonth - 1).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric'
            })} - {new Date(endYear, endMonth - 1).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric'
            })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
